import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import type { Event, Graph, WithContext } from 'schema-dts'
import type { AlgoliaEventItem } from '~/composables/types/algolia'

const useAlgoliaEventSchema = (
  events: AlgoliaEventItem[],
  overwrites: any = undefined
): any => {
  const schema = ref<Graph | undefined>(undefined)
  const script = computed(() => ({
    type: 'application/ld+json',
    innerHTML: schema.value,
  }))

  const eventAttendanceModeOptions = {
    HYBRID: 'https://schema.org/MixedEventAttendanceMode',
    IN_PERSON: 'https://schema.org/OfflineEventAttendanceMode',
    ONLINE: 'https://schema.org/OnlineEventAttendanceMode',
  }

  const fullLocalizedUrl = useFullLocalizedUrl()
  const {
    public: { baseUrl },
  } = useRuntimeConfig()

  const createSchema = (): Event[] => {
    function formatStartDate(startDate: number | undefined, hasDates: boolean) {
      if (!startDate) return
      dayjs.extend(utc)
      dayjs.extend(timezone)

      return hasDates
        ? dayjs(startDate * 1000)
            .tz('Europe/Berlin')
            .toISOString()
        : dayjs().tz('Europe/Berlin').add(1, 'month').toISOString()
    }

    return events.map((event) => {
      const hasDates = Boolean(
        algoliaEventGetters.fnGetNextDateAvailableStart(event)
      )

      const supplierUrl =
        algoliaEventGetters.getSupplierPermalink(event) &&
        fullLocalizedUrl(
          `/p/${algoliaEventGetters.getSupplierPermalink(event)}/`
        )

      const obj: WithContext<Event> = {
        '@type': 'Event',
        name: algoliaEventGetters.fnGetTitle(event),
        url: fullLocalizedUrl(
          `/e/${algoliaEventGetters.fnGetPermalink(event)}/`
        ),
        startDate: formatStartDate(
          algoliaEventGetters.fnGetNextDateAvailableStart(event),
          hasDates
        ),
        endDate: formatStartDate(
          algoliaEventGetters.fnGetNextDateAvailableEnd(event),
          hasDates
        ),
        eventAttendanceMode:
          eventAttendanceModeOptions[algoliaEventGetters.fnGetType(event)] ||
          '',
        // TODO dynamic eventStatus
        eventStatus: 'https://schema.org/EventScheduled',
        image: [algoliaEventGetters.getPrincipalImage(event)],
        description: algoliaEventGetters.fnGetSubtitle(event),
        offers: {
          '@type': 'Offer',
          availability: 'InStock',
          price: algoliaEventGetters.fnGetMinimumPriceAmount(event) / 100,
          url: fullLocalizedUrl(
            `/e/${algoliaEventGetters.fnGetPermalink(event)}/`
          ),
          priceCurrency: algoliaEventGetters.fnGetMinimumPriceCurrency(event),
        },
        performer: {
          '@type': 'PerformingGroup',
          name: algoliaEventGetters.fnGetSupplier(event),
          url: supplierUrl,
        },
        organizer: {
          '@type': 'Organization',
          name: algoliaEventGetters.fnGetSupplier(event),
          url: supplierUrl,
        },

        ...(algoliaEventGetters.getAverageRating(event) &&
          algoliaEventGetters.getRatingsAmount(event) > 0 && {
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: algoliaEventGetters.getAverageRating(event, 'en-US'),
              bestRating: 5,
              worstRating: 1,
              ratingCount: algoliaEventGetters.getRatingsAmount(event),
            },
          }),
      }

      if (algoliaEventGetters.fnGetType(event) !== 'ONLINE') {
        obj.location = {
          '@type': 'Place',
          name:
            overwrites?.locality?.name ||
            algoliaEventGetters.fnGetCitySlug(event),
          address: {
            '@type': 'PostalAddress',
            name: algoliaEventGetters.fnGetFormattedAddress(event),
            postalCode: algoliaEventGetters.fnGetFormattedZipcode(event),
          },
        }
      } else {
        obj.location = {
          '@type': 'VirtualLocation',
          url: baseUrl,
        }
      }

      return obj as WithContext<Event>
    })
  }

  schema.value = { '@context': 'https://schema.org', '@graph': createSchema() }

  return {
    schema,
    script,
  }
}

export default useAlgoliaEventSchema
